/**
 * Complete the look on the wall art product page (snippets/c365-wall-look.liquid, brief §10).
 *
 * Looks come from c365.looks (tools/wall-art/looks.py): slot 0 is this artwork (locked), the others can be swapped
 * for an alternative or removed. The scene is drawn to scale (inches) above an 84″ sofa on the shopper's wall color
 * (localStorage "c365.wall", shared with the collection page picker). Canvas / Framed and the frame apply to the whole
 * look. "Add the look" adds every piece with the same line properties as the product page, plus _look.
 * Bundle tiers (docs/wall-art/decisions.md): 2 → 15%, 3 → 20%, 4 → 25%, applied at checkout by automatic discounts.
 */
(() => {
  const TIERS = { 2: 15, 3: 20, 4: 25 };
  const WALLS = [['White', '#f3f1ec'], ['Warm gray', '#cdc5b9'], ['Cool gray', '#b8bdc2'], ['Beige', '#e4d6c0'],
    ['Sage', '#b4bea4'], ['Navy', '#2f3e55'], ['Charcoal', '#3a3c3f']];
  const WALL_W = 150; // inches of wall shown across the scene
  const SOFA_W = 84, SOFA_H = 32, ABOVE_SOFA = 10;
  const money = (n) => '$' + n.toFixed(2);
  const sizeLabel = (s) => s.replace('x', '×') + '″';
  const readWall = () => { try { return localStorage.getItem('c365.wall'); } catch (e) { return null; } };
  const writeWall = (v) => { try { localStorage.setItem('c365.wall', v); } catch (e) { /* private mode */ } };
  // Liquid's t filter HTML-escapes ' " & < >; the strings here go to textContent / template literals, so undo it
  const D = (s) => (typeof s === 'string' ? s.replace(/&(#39|#34|quot|amp|lt|gt);/g, (m, e) => ({ '#39': "'", '#34': '"', quot: '"', amp: '&', lt: '<', gt: '>' })[e]) : s);
  // text in the visitor's language: window.C365_T (c365.js.wall.* from snippets/c365-wall-look.liquid, "label.<English
  // value>" for store data), English without it
  const T = (k, f, vars) =>
    String(D(window.C365_T && window.C365_T[k]) || f).replace(/\{(\w+)\}/g, (m, n) => (vars && n in vars ? vars[n] : m));
  const L = (s) => D(s && window.C365_T && window.C365_T[`label.${s}`]) || s;
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  class WallLook extends HTMLElement {
    connectedCallback() {
      const data = JSON.parse(this.querySelector('[data-look-data]').textContent);
      this.products = data.products;
      // drop pieces / alternatives whose product isn't available here, then looks that lost a piece
      this.looks = data.looks
        .map((lk) => ({ ...lk, slots: lk.slots.map((s) => ({ ...s, alts: (s.alts || []).filter((a) => this.products[a.handle]) })) }))
        .filter((lk) => lk.slots.every((s, i) => i === 0 || this.products[s.handle]));
      if (!this.looks.length) { this.closest('section')?.remove(); return; }
      this.look = 0;
      this.removed = new Set();
      this.type = 'canvas';
      this.frame = this.querySelector('[data-look-frame]')?.dataset.lookFrame || 'Black';
      this.frameHex = this.querySelector('[data-look-frame]')?.dataset.hex || '#1d1d1d';
      this.wall = WALLS.find((w) => w[0] === readWall()) || WALLS[1];
      this.swapSlot = null;
      this.bind();
      this.renderAll();
    }

    $(s) { return this.querySelector(s); }
    $$(s) { return [...this.querySelectorAll(s)]; }

    get current() { return this.looks[this.look]; }
    get pieces() { return this.current.slots.filter((s, i) => i === 0 || !this.removed.has(i)); }

    bind() {
      this.$$('[data-look-type]').forEach((b) => b.addEventListener('click', () => {
        this.type = b.dataset.lookType;
        this.$$('[data-look-type]').forEach((x) => x.classList.toggle('is-active', x === b));
        this.$('[data-look-frames]').hidden = this.type !== 'framed';
        this.renderAll();
      }));
      this.$$('[data-look-frame]').forEach((b) => b.addEventListener('click', () => {
        this.frame = b.dataset.lookFrame;
        this.frameHex = b.dataset.hex;
        this.$$('[data-look-frame]').forEach((x) => x.classList.toggle('is-active', x === b));
        this.renderAll();
      }));
      this.$$('[data-look-add]').forEach((b) => b.addEventListener('click', () => this.add()));
      window.addEventListener('resize', () => this.renderScene());
      const sticky = this.$('[data-look-sticky]');
      if ('IntersectionObserver' in window && sticky) {
        new IntersectionObserver(([e]) => { sticky.hidden = !(e.isIntersecting && window.matchMedia('(max-width: 749px)').matches); })
          .observe(this);
      }
    }

    price(handle, size) {
      const row = this.products[handle]?.sizes?.find((r) => r.size === size);
      return Number(row?.[this.type] || row?.canvas || 0);
    }

    renderAll() {
      this.renderTabs();
      this.renderWalls();
      this.renderScene();
      this.renderItems();
      this.renderSum();
    }

    renderTabs() {
      const tabs = this.$('[data-look-tabs]');
      tabs.innerHTML = '';
      this.looks.forEach((lk, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'c365-chip' + (i === this.look ? ' is-active' : '');
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-selected', String(i === this.look));
        b.textContent = L(lk.label);
        b.addEventListener('click', () => { this.look = i; this.removed.clear(); this.swapSlot = null; this.renderAll(); });
        tabs.append(b);
      });
    }

    renderWalls() {
      const box = this.$('[data-look-walls]');
      box.innerHTML = '';
      for (const w of WALLS) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'c365-look__wall' + (w === this.wall ? ' is-active' : '');
        b.style.background = w[1];
        b.title = b.ariaLabel = T('wall.wall_name', '{color} wall', { color: L(w[0]) });
        b.addEventListener('click', () => { this.wall = w; writeWall(w[0]); this.renderWalls(); this.renderScene(); });
        box.append(b);
      }
    }

    renderScene() {
      const scene = this.$('[data-look-scene]');
      const art = this.$('[data-look-art]');
      scene.style.background = this.wall[1];
      const W = scene.clientWidth, H = scene.clientHeight;
      const ppi = W / WALL_W;
      const sofa = this.$('.c365-look__sofa');
      sofa.style.width = SOFA_W * ppi + 'px';
      sofa.style.height = SOFA_H * ppi + 'px';
      const pieces = this.pieces;
      const aw = Math.max(...pieces.map((s) => s.x + s.w));
      const ah = Math.max(...pieces.map((s) => s.y + s.h));
      const floor = H * 0.13;
      const bottom = H - floor - SOFA_H * ppi * 0.92 - ABOVE_SOFA * ppi;
      const left = (W - aw * ppi) / 2, top = Math.max(8, bottom - ah * ppi);
      const fw = this.type === 'framed' ? Math.max(2, 1.25 * ppi) : 0;
      art.innerHTML = '';
      this.current.slots.forEach((s, i) => {
        if (i > 0 && this.removed.has(i)) return;
        const p = i === 0 ? this.products[s.handle] || { img: this.anchorImg() } : this.products[s.handle];
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'c365-look__piece' + (i === 0 ? ' is-anchor' : '') + (this.swapSlot === i ? ' is-swapping' : '');
        Object.assign(b.style, { left: left + s.x * ppi + 'px', top: top + s.y * ppi + 'px', width: s.w * ppi + 'px', height: s.h * ppi + 'px' });
        if (fw) b.style.outline = `${fw}px solid ${this.frameHex}`;
        b.innerHTML = `<img src="${p?.img || ''}" alt="" loading="lazy">`;
        b.ariaLabel = i === 0
          ? `${p?.title || T('wall.this_artwork', 'This artwork')}, ${sizeLabel(s.size)}`
          : T('wall.swap_title', 'Swap {title}', { title: p.title });
        if (i > 0) b.addEventListener('click', () => this.openSwap(i));
        art.append(b);
      });
      this.$('[data-look-why]').textContent = T('wall.look_why', '{look} · {width}″ wide · {wall} wall', {
        look: L(this.current.label), width: Math.round(aw), wall: L(this.wall[0]).toLowerCase(),
      });
    }

    anchorImg() {
      return document.querySelector('[data-mockup-img="true"]')?.currentSrc || '';
    }

    renderItems() {
      const ul = this.$('[data-look-items]');
      ul.innerHTML = '';
      this.current.slots.forEach((s, i) => {
        if (i > 0 && this.removed.has(i)) return;
        const p = this.products[s.handle] || {};
        const li = document.createElement('li');
        li.className = 'c365-look__item';
        li.innerHTML = `<img src="${p.img || this.anchorImg()}" alt="">
          <span class="c365-look__item-text"><b></b><small></small></span>
          <span class="c365-look__item-price">${money(this.price(s.handle, s.size))}</span>`;
        li.querySelector('b').textContent = p.title || document.querySelector('.c365-buy h1')?.textContent || '';
        li.querySelector('small').textContent = `${sizeLabel(s.size)} · ${i === 0 ? T('wall.this_artwork', 'This artwork')
          : this.type === 'framed' ? T('wall.frame_value', '{frame} frame', { frame: L(this.frame) }) : L('Canvas Print')}`;
        if (i > 0) {
          const actions = document.createElement('span');
          actions.className = 'c365-look__item-actions';
          if (s.alts?.length) {
            const sw = document.createElement('button');
            sw.type = 'button';
            sw.className = 'c365-link-btn';
            sw.textContent = T('wall.swap', 'Swap');
            sw.addEventListener('click', () => this.openSwap(i));
            actions.append(sw);
          }
          const rm = document.createElement('button');
          rm.type = 'button';
          rm.className = 'c365-look__rm';
          rm.ariaLabel = T('wall.remove_title', 'Remove {title}', { title: p.title });
          rm.textContent = '×';
          rm.addEventListener('click', () => { this.removed.add(i); this.swapSlot = null; this.renderAll(); });
          actions.append(rm);
          li.append(actions);
        }
        ul.append(li);
      });
      // removed pieces can come back
      this.current.slots.forEach((s, i) => {
        if (!this.removed.has(i)) return;
        const li = document.createElement('li');
        li.className = 'c365-look__item is-removed';
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'c365-link-btn';
        b.textContent = T('wall.add_back', '+ Add {title} back', { title: this.products[s.handle]?.title || T('wall.it', 'it') });
        b.addEventListener('click', () => { this.removed.delete(i); this.renderAll(); });
        li.append(b);
        ul.append(li);
      });
      this.renderSwap();
    }

    openSwap(i) {
      this.swapSlot = this.swapSlot === i ? null : i;
      this.renderScene();
      this.renderSwap();
    }

    renderSwap() {
      const box = this.$('[data-look-swap]');
      const slot = this.swapSlot != null ? this.current.slots[this.swapSlot] : null;
      box.hidden = !slot?.alts?.length;
      if (box.hidden) return;
      const alts = this.$('[data-look-alts]');
      alts.innerHTML = '';
      for (const a of slot.alts) {
        const p = this.products[a.handle];
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'c365-look__alt';
        b.innerHTML = `<img src="${p.img}" alt=""><span></span>`;
        b.lastElementChild.textContent = p.title;
        b.addEventListener('click', () => {
          // swap places: the old piece becomes an alternative
          const old = { handle: slot.handle, size: slot.size };
          slot.alts = slot.alts.filter((x) => x !== a).concat(old);
          slot.handle = a.handle;
          slot.size = this.products[a.handle].sizes.some((r) => r.size === a.size) ? a.size : slot.size;
          this.swapSlot = null;
          this.renderAll();
        });
        alts.append(b);
      }
    }

    totals() {
      const pieces = this.pieces;
      const full = pieces.reduce((t, s) => t + this.price(s.handle, s.size), 0);
      const pct = TIERS[Math.min(pieces.length, 4)] || 0;
      return { n: pieces.length, full, pct, off: (full * pct) / 100 };
    }

    renderSum() {
      const t = this.totals();
      const sum = this.$('[data-look-sum]');
      sum.innerHTML = t.pct
        ? `<div><span>${esc(T('wall.bought_separately', 'Bought separately'))}</span><s>${money(t.full)}</s></div>
           <div class="is-save"><span>${esc(T('wall.look_discount', 'Look discount −{pct}%', { pct: t.pct }))}</span><span>−${money(t.off)}</span></div>
           <div class="is-total"><span>${esc(T('wall.look_price', 'Look price'))}</span><b>${money(t.full - t.off)}</b></div>`
        : `<div class="is-total"><span>${esc(T('wall.total', 'Total'))}</span><b>${money(t.full)}</b></div>`;
      const next = TIERS[t.n + 1];
      if (next && t.n < this.current.slots.length) {
        sum.insertAdjacentHTML('beforeend', `<p class="c365-look__next">${esc(T('wall.add_one_more', 'Add 1 more artwork, save {pct}%', { pct: next }))}</p>`);
      }
      const label = t.pct ? T('wall.add_look_save', 'Add the look · Save {amount}', { amount: money(t.off) }) : T('wall.add_to_cart', 'Add to cart');
      this.$$('[data-look-add]').forEach((b) => (b.textContent = label));
      this.$('[data-look-sticky-text]').textContent = t.n === 1
        ? T('wall.sticky_count_one', '{n} artwork · {price}', { n: t.n, price: money(t.full - t.off) })
        : T('wall.sticky_count_other', '{n} artworks · {price}', { n: t.n, price: money(t.full - t.off) });
    }

    async add() {
      const err = this.$('[data-look-error]');
      err.hidden = true;
      const lookId = `look-${Date.now().toString(36)}`;
      const items = this.pieces.map((s) => {
        const p = this.products[s.handle];
        const row = p?.sizes?.find((r) => r.size === s.size);
        const props = { 'Product type': this.type === 'framed' ? 'Framed Canvas' : 'Canvas Print', Size: s.size.replace('x', '×'),
          _size_code: row?.code || s.size.replace('x', ''), _look: lookId };
        if (this.type === 'framed') props.Frame = this.frame;
        return { id: p?.variant, quantity: 1, properties: props };
      }).filter((it) => it.id);
      this.$$('[data-look-add]').forEach((b) => (b.disabled = true));
      try {
        const res = await fetch(`${this.dataset.cartAdd}.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error((await res.json()).description || T('wall.add_look_error', 'Could not add the look'));
        location.assign(this.dataset.cart);
      } catch (e) {
        err.textContent = e.message;
        err.hidden = false;
        this.$$('[data-look-add]').forEach((b) => (b.disabled = false));
      }
    }
  }

  customElements.get('c365-wall-look') || customElements.define('c365-wall-look', WallLook);
})();
